import React, { FC, useState } from 'react';
import { Formik, getIn } from 'formik';
import * as Yup from 'yup';
import { Button, Asset, InfoFilledIcon, Tooltip } from '@clearcover/atlas';
import classnames from 'classnames';

import { Column, FormInput, Row } from 'Shared/components';
import { formatPhoneNumber } from 'Shared/utils';
import {
  formatAddressDisplay,
  getContactInfoByFieldValue,
} from 'QuoteApp/utils';
import { RoutePrompt } from 'QuoteApp/components';
import { useAppContext, usePurchaseContext } from 'QuoteApp/services/providers';
import {
  sanitizeNumString,
  sanitizeString,
} from 'QuoteApp/services/endpoints/utils';

import styles from './styles.module.css';

type ContactInfoV2Type = {
  setIsEditing: (isEditing: boolean) => void;
};

export const ContactInfoV2: FC<ContactInfoV2Type> = ({ setIsEditing }) => {
  const { details, api, fetchRate, refreshDetails } = useAppContext();
  const { getSignaturePacket } = usePurchaseContext();

  const [isEditingPhoneNumber, setIsEditingPhoneNumber] = useState<boolean>(
    false
  );
  const [isEditingEmail, setIsEditingEmail] = useState<boolean>(false);

  const contactInfoSchema = Yup.object().shape({
    contactInfo: Yup.object().shape({
      [details.principalInsuredId]: Yup.object().shape({
        email: Yup.string()
          .email('Must be a valid email address')
          .required('Email address is required'),
        phoneNumber: Yup.string()
          .transform((value: string) => value.replace(/[^0-9.]/g, ''))
          .min(10, 'Must be 10 numeric digits')
          .required('Phone Number is required'),
      }),
    }),
  });

  return (
    <Formik
      enableReinitialize
      initialValues={{
        contactInfo: getContactInfoByFieldValue(details.contactInfo, 'value'),
      }}
      validationSchema={contactInfoSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await api.saveAppData({
            data: {
              insurableParties: [
                {
                  identifier: details.principalInsuredId,
                  contactInformation: {
                    email: sanitizeString(
                      values.contactInfo[details.principalInsuredId].email
                    ),
                    telephone: {
                      phoneType: sanitizeString(
                        details.contactInfo[details.principalInsuredId]
                          .phoneType.value
                      ),
                      number: sanitizeNumString(
                        values.contactInfo[details.principalInsuredId]
                          .phoneNumber
                      ),
                    },
                  },
                },
              ],
            },
          });

          await fetchRate();
          refreshDetails();
          getSignaturePacket();

          setIsEditing(false);
          setIsEditingPhoneNumber(false);
          setIsEditingEmail(false);
        } catch {
          // api.saveAppData will throw a toast error
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({
        values,
        touched,
        errors,
        dirty,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValid,
        resetForm,
      }) => {
        const resetFields = () => {
          resetForm();
          setIsEditingEmail(false);
          setIsEditingPhoneNumber(false);
          setIsEditing(false);
        };

        return (
          <>
            <RoutePrompt
              when={dirty && !isSubmitting}
              editingIdDisplay=""
              isValid={isValid}
            />
            <h3 className={styles.contactInfoHeading}>
              Confirm contact information for communication and documents
            </h3>
            <div className={styles.contactInfoForm2}>
              <Row className={styles.contactInfoRow}>
                <Column className={styles.contactInfoFormColumn}>
                  <div className={styles.contactInfoLabel}>Phone Number</div>
                </Column>
                <Column className={styles.contactInfoFormColumn}>
                  {isEditingPhoneNumber ? (
                    <FormInput
                      data-testid="phone-number"
                      variant="text"
                      name={`contactInfo.${[
                        details.principalInsuredId,
                      ]}.phoneNumber`}
                      value={formatPhoneNumber(
                        values.contactInfo[details.principalInsuredId]
                          .phoneNumber
                      )}
                      className={styles.contactInfoEditInput}
                      label="Phone Number"
                      disabled={isSubmitting}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      maxLength={12}
                      errorMessage={
                        getIn(
                          touched,
                          `contactInfo.${[
                            details.principalInsuredId,
                          ]}.phoneNumber`
                        )
                          ? getIn(
                              errors,
                              `contactInfo.${[
                                details.principalInsuredId,
                              ]}.phoneNumber`
                            )
                          : undefined
                      }
                    />
                  ) : (
                    <span
                      className={classnames('fs-mask', styles.contactInfoValue)}
                    >
                      {formatPhoneNumber(
                        details.contactInfo[details.principalInsuredId]
                          .phoneNumber.value || ''
                      )}
                    </span>
                  )}
                </Column>
                <Column className={styles.contactInfoFormColumn}>
                  {isEditingPhoneNumber && (
                    <Button
                      link
                      size="md"
                      className={styles.contactInfoEditButton}
                      onClick={resetFields}
                      disabled={isSubmitting}
                      data-testid="cancel-edit-phone-number"
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    link
                    size="md"
                    className={styles.contactInfoEditButton}
                    disabled={
                      isEditingPhoneNumber && (isSubmitting || !isValid)
                    }
                    onClick={() => {
                      if (isEditingPhoneNumber) {
                        handleSubmit();
                      } else {
                        resetFields();
                        setIsEditingPhoneNumber(true);
                        setIsEditing(true);
                      }
                    }}
                    type={isEditingPhoneNumber ? 'submit' : 'button'}
                    data-testid="phone-number-edit-button"
                  >
                    {isEditingPhoneNumber ? 'Save' : 'Edit'}
                  </Button>
                </Column>
              </Row>
              <Row className={styles.contactInfoRow}>
                <Column className={styles.contactInfoFormColumn}>
                  <div className={styles.contactInfoLabel}>Email Address</div>
                </Column>
                <Column className={styles.contactInfoFormColumn}>
                  {isEditingEmail ? (
                    <FormInput
                      data-testid="email-address"
                      variant="text"
                      name={`contactInfo.${[details.principalInsuredId]}.email`}
                      value={
                        values.contactInfo[details.principalInsuredId].email
                      }
                      label="Email Address"
                      disabled={isSubmitting}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errorMessage={
                        getIn(
                          touched,
                          `contactInfo.${[details.principalInsuredId]}.email`
                        )
                          ? getIn(
                              errors,
                              `contactInfo.${[
                                details.principalInsuredId,
                              ]}.email`
                            )
                          : undefined
                      }
                    />
                  ) : (
                    <span
                      className={classnames('fs-mask', styles.contactInfoValue)}
                    >
                      {details.contactInfo[details.principalInsuredId].email
                        .value || ''}
                    </span>
                  )}
                </Column>
                <Column className={styles.contactInfoFormColumn}>
                  {isEditingEmail && (
                    <Button
                      link
                      size="md"
                      className={styles.contactInfoEditButton}
                      onClick={resetFields}
                      disabled={isSubmitting}
                      data-testid="cancel-edit-email-address"
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    link
                    size="md"
                    className={styles.contactInfoEditButton}
                    disabled={isEditingEmail && (isSubmitting || !isValid)}
                    onClick={() => {
                      if (isEditingEmail) {
                        handleSubmit();
                      } else {
                        resetFields();
                        setIsEditingEmail(true);
                        setIsEditing(true);
                      }
                    }}
                    type={isEditingEmail ? 'submit' : 'button'}
                    data-testid="email-edit-button"
                  >
                    {isEditingEmail ? 'Save' : 'Edit'}
                  </Button>
                </Column>
              </Row>
              <Row className={styles.contactInfoRow}>
                <Column className={styles.contactInfoFormColumn}>
                  <div className={styles.contactInfoLabel}>Primary Address</div>
                </Column>
                <Column className={styles.contactInfoFormColumn}>
                  <span
                    className={classnames('fs-mask', styles.contactInfoValue)}
                  >
                    {formatAddressDisplay(
                      details.address[details.principalInsuredId]
                    )}
                  </span>
                </Column>
                <Column className={styles.contactInfoFormColumn}>
                  <Tooltip
                    tooltip={
                      <div className={styles.primaryAddressInfoTooltip}>
                        Please go to the Summary screen to update the
                        policyholder address.
                      </div>
                    }
                    position="top"
                  >
                    <Asset
                      icon={InfoFilledIcon}
                      height={18}
                      width={18}
                      description="Primary Address Information"
                      className={styles.primaryAddressInfoIcon}
                    />
                  </Tooltip>
                </Column>
              </Row>
            </div>
          </>
        );
      }}
    </Formik>
  );
};
